// 生成/转发表格文档工具：xlsx（Excel 直接打开）优先，csv 兜底
import * as XLSX from 'xlsx';

// 生成 xlsx 二进制（浏览器端用 SheetJS）
// expiryNote: 可选字符串，作为文档绝对第一行（如"数据有效期至2026-09-22 10:00，非有效期内提交无效"）
// 数据列头「手机号」；每 10 行插入一个空行，方便查看不串行
export function buildXlsx(rows = [], expiryNote = null) {
  const phones = rows.map(r => r.phone || '');
  const aoa = [];
  if (expiryNote) {
    aoa.push([`数据有效期至${expiryNote}，非有效期内提交无效`]);
    aoa.push([]); // 空行分隔，号码从第3行开始
  }
  aoa.push(['手机号']);
  phones.forEach((p, i) => {
    aoa.push([p]);
    // 每满 10 行（且不是最后一行）插一个空行
    if ((i + 1) % 10 === 0 && i !== phones.length - 1) aoa.push([]);
  });
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // 手机号列保持文本，避免 Excel 把长数字当科学计数法
  ws['!cols'] = [{ wch: 16 }];
  XLSX.utils.book_append_sheet(wb, ws, '数据');
  return XLSX.write(wb, { type: 'buffer', bookType: 'xlsx' }); // ArrayBuffer
}

// 生成 CSV 文本（兜底）：同样只有手机号一列，每 10 行空一行
export function buildCsv(rows = []) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = ['手机号'];
  rows.forEach((r, i) => {
    lines.push([r.phone || ''].map(esc).join(','));
    if ((i + 1) % 10 === 0 && i !== rows.length - 1) lines.push('');
  });
  return '\ufeff' + lines.join('\r\n');
}

// 生成纯文本文档（号码一列一行）
export function buildNumberText(phones = []) {
  return phones.filter(Boolean).join('\r\n');
}

// 把文档文件转发到其他 App（微信/QQ 等）
// 优先 navigator.share({files})：调起系统分享面板 → 选微信/QQ 即可发送文件；
// 不支持文件分享时回退为直接下载（<a download> 保存到本地）。
// 返回 'shared' | 'cancelled' | 'download'
export async function shareTextFile({ filename = '数据.csv', text = '', mime = 'text/csv' }) {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const file = new File([blob], filename, { type: mime });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: filename });
      return 'shared';
    } catch (e) {
      if (e?.name === 'AbortError') return 'cancelled';
    }
  }
  downloadFile({ filename, text, mime });
  return 'download';
}

// xlsx 分享/下载：text 为 ArrayBuffer（buildXlsx 的返回值）
export async function shareFile({ filename = '数据.xlsx', text = null, mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }) {
  if (text == null) return 'cancelled';
  const arr = text instanceof ArrayBuffer ? text : new Uint8Array(text).buffer;
  const blob = new Blob([arr], { type: mime });
  const file = new File([blob], filename, { type: mime });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: filename });
      return 'shared';
    } catch (e) {
      if (e?.name === 'AbortError') return 'cancelled';
    }
  }
  downloadFile({ filename, text: arr, mime });
  return 'download';
}

// 直接下载文件（ArrayBuffer / Blob 支持）
export function downloadFile({ filename = '数据.xlsx', text = null, mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }) {
  if (text == null) return;
  const arr = text instanceof ArrayBuffer ? text : new Uint8Array(text).buffer;
  const blob = new Blob([arr], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 800);
}
